/**
 * Document Conversion Page
 * Convert handwritten notes and uploaded documents to clinical text
 */

import React, { useState, useRef, useCallback } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import api from '../../api';
import {
  FileText, Upload, Camera, Scan, RefreshCw, Copy, Check,
  Download, Eye, Edit3, Save, X, Sparkles, AlertTriangle,
  ClipboardList, FileImage, Loader2
} from 'lucide-react';

const DocumentConversionPage = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [converting, setConverting] = useState(false);
  const [conversionResult, setConversionResult] = useState(null);
  const [editedText, setEditedText] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [documentType, setDocumentType] = useState('clinical_note');
  const [additionalContext, setAdditionalContext] = useState('');
  const [copied, setCopied] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  // Handle file selection
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Please upload a JPEG, PNG, WEBP, or PDF file');
        return;
      }

      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size must be less than 10MB');
        return;
      }

      setSelectedFile(file);
      setConversionResult(null);
      
      // Create preview URL
      if (file.type.startsWith('image/')) {
        const url = URL.createObjectURL(file);
        setPreviewUrl(url);
      } else {
        setPreviewUrl(null);
      }
    }
  };

  // Handle camera capture
  const handleCameraCapture = () => {
    cameraInputRef.current?.click();
  };

  // Convert document
  const handleConvert = async () => {
    if (!selectedFile) {
      toast.error('Please select a file first');
      return;
    }

    setConverting(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('document_type', documentType);
      if (additionalContext) {
        formData.append('additional_context', additionalContext);
      }

      const response = await api.post('/documents/convert-handwritten', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success) {
        setConversionResult(response.data);
        setEditedText(response.data.converted_text);
        toast.success('Document converted successfully!');
      }
    } catch (error) {
      console.error('Conversion error:', error);
      toast.error(error.response?.data?.detail || 'Failed to convert document');
    } finally {
      setConverting(false);
    }
  };

  // Copy to clipboard
  const handleCopy = async () => {
    const textToCopy = isEditing ? editedText : conversionResult?.converted_text;
    await navigator.clipboard.writeText(textToCopy);
    setCopied(true);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  // Download as text file
  const handleDownload = () => {
    const textToDownload = isEditing ? editedText : conversionResult?.converted_text;
    const blob = new Blob([textToDownload], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `converted-${conversionResult?.document_id || 'document'}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Document downloaded');
  };

  // Reset form
  const handleReset = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setConversionResult(null);
    setEditedText('');
    setIsEditing(false);
    setAdditionalContext('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Get confidence color
  const getConfidenceColor = (confidence) => {
    if (confidence >= 0.9) return 'text-green-600 bg-green-100';
    if (confidence >= 0.7) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="document-conversion-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Scan className="w-6 h-6 text-purple-600" />
              Document Conversion
            </h1>
            <p className="text-gray-600 mt-1">
              Convert handwritten notes and scanned documents to typed clinical text
            </p>
          </div>
          {conversionResult && (
            <Button variant="outline" onClick={handleReset}>
              <RefreshCw className="w-4 h-4 mr-2" />
              New Conversion
            </Button>
          )}
        </div>

        {/* Info Banner */}
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 flex items-start gap-3">
          <Sparkles className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-purple-800">AI-Powered Document Conversion</p>
            <p className="text-sm text-purple-600">
              Upload handwritten clinical notes, scanned documents, or photos. Our AI will convert them 
              to structured, typed clinical documentation that can be edited and added to patient records.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Upload Section */}
          <div className="bg-white rounded-lg border p-6 space-y-4">
            <h2 className="font-semibold text-gray-900 flex items-center gap-2">
              <Upload className="w-5 h-5 text-blue-600" />
              Upload Document
            </h2>

            {/* File Input */}
            <div 
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                selectedFile ? 'border-green-400 bg-green-50' : 'border-gray-300 hover:border-blue-400'
              }`}
              onClick={() => fileInputRef.current?.click()}
              style={{ cursor: 'pointer' }}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,application/pdf"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="file-input"
              />
              
              {selectedFile ? (
                <div>
                  <FileImage className="w-12 h-12 mx-auto text-green-600 mb-2" />
                  <p className="font-medium text-green-800">{selectedFile.name}</p>
                  <p className="text-sm text-green-600">
                    {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
              ) : (
                <div>
                  <Upload className="w-12 h-12 mx-auto text-gray-400 mb-2" />
                  <p className="font-medium text-gray-700">Click to upload or drag & drop</p>
                  <p className="text-sm text-gray-500">JPEG, PNG, WEBP, or PDF (max 10MB)</p>
                </div>
              )}
            </div>

            {/* Camera Capture */}
            <div className="flex gap-2">
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleFileSelect}
                className="hidden"
              />
              <Button 
                variant="outline" 
                onClick={handleCameraCapture}
                className="flex-1"
              >
                <Camera className="w-4 h-4 mr-2" />
                Take Photo
              </Button>
              <Button 
                variant="outline" 
                onClick={() => fileInputRef.current?.click()}
                className="flex-1"
              >
                <FileText className="w-4 h-4 mr-2" />
                Browse Files
              </Button>
            </div>

            {/* Preview */}
            {previewUrl && (
              <div className="mt-4">
                <Label className="text-sm text-gray-600">Preview:</Label>
                <img 
                  src={previewUrl} 
                  alt="Preview" 
                  className="mt-2 max-h-64 rounded-lg border mx-auto"
                />
              </div>
            )}

            {/* Document Type */}
            <div>
              <Label>Document Type</Label>
              <select
                value={documentType}
                onChange={(e) => setDocumentType(e.target.value)}
                className="w-full mt-1 px-3 py-2 border rounded-md"
                data-testid="document-type-select"
              >
                <option value="clinical_note">Clinical Note (Structured)</option>
                <option value="general">General Text Extraction</option>
              </select>
            </div>

            {/* Additional Context */}
            <div>
              <Label>Additional Context (Optional)</Label>
              <Input
                value={additionalContext}
                onChange={(e) => setAdditionalContext(e.target.value)}
                placeholder="e.g., Patient visit for wound care follow-up"
                className="mt-1"
              />
            </div>

            {/* Convert Button */}
            <Button 
              onClick={handleConvert}
              disabled={!selectedFile || converting}
              className="w-full bg-purple-600 hover:bg-purple-700"
              data-testid="convert-btn"
            >
              {converting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Converting with AI...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Convert Document
                </>
              )}
            </Button>
          </div>

          {/* Results Section */}
          <div className="bg-white rounded-lg border p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                <ClipboardList className="w-5 h-5 text-green-600" />
                Converted Text
              </h2>
              
              {conversionResult && (
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${getConfidenceColor(conversionResult.confidence)}`}>
                    {Math.round(conversionResult.confidence * 100)}% confidence
                  </span>
                </div>
              )}
            </div>

            {conversionResult ? (
              <>
                {/* Action Buttons */}
                <div className="flex gap-2 flex-wrap">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsEditing(!isEditing)}
                  >
                    {isEditing ? <Eye className="w-4 h-4 mr-1" /> : <Edit3 className="w-4 h-4 mr-1" />}
                    {isEditing ? 'View' : 'Edit'}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCopy}
                  >
                    {copied ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
                    {copied ? 'Copied!' : 'Copy'}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDownload}
                  >
                    <Download className="w-4 h-4 mr-1" />
                    Download
                  </Button>
                </div>

                {/* Text Display/Edit */}
                {isEditing ? (
                  <textarea
                    value={editedText}
                    onChange={(e) => setEditedText(e.target.value)}
                    className="w-full h-96 p-4 border rounded-lg font-mono text-sm resize-none"
                    data-testid="edit-textarea"
                  />
                ) : (
                  <div 
                    className="h-96 overflow-y-auto p-4 bg-gray-50 rounded-lg border text-sm whitespace-pre-wrap"
                    data-testid="converted-text-display"
                  >
                    {conversionResult.converted_text}
                  </div>
                )}

                {/* Document Info */}
                <div className="text-xs text-gray-500 space-y-1">
                  <p>Document ID: {conversionResult.document_id}</p>
                  <p>Original File: {conversionResult.original_filename}</p>
                  <p>Converted: {new Date(conversionResult.timestamp).toLocaleString()}</p>
                </div>

                {/* Warning for low confidence */}
                {conversionResult.confidence < 0.7 && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-yellow-700">
                      Low confidence conversion. Please review carefully for accuracy. 
                      Some text may be illegible or incorrectly interpreted.
                    </p>
                  </div>
                )}
              </>
            ) : (
              <div className="h-96 flex items-center justify-center text-gray-400">
                <div className="text-center">
                  <FileText className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p>Upload a document to see converted text</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Supported Formats */}
        <div className="bg-gray-50 rounded-lg border p-4">
          <h3 className="font-medium text-gray-900 mb-2">Supported Formats & Tips</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <p className="font-medium text-gray-700">📷 Images</p>
              <p className="text-gray-500">JPEG, PNG, WEBP - Best for photos of handwritten notes</p>
            </div>
            <div>
              <p className="font-medium text-gray-700">📄 Documents</p>
              <p className="text-gray-500">PDF - Best for scanned documents (first page only)</p>
            </div>
            <div>
              <p className="font-medium text-gray-700">💡 Tips</p>
              <p className="text-gray-500">Good lighting, clear handwriting, and flat surface improve accuracy</p>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default DocumentConversionPage;
